import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { DialogMessage } from '../../dialog/dialog.model';
import { DialogService } from '../../dialog/dialog.service';
import { Observable, Subscription } from '../../util/rxjs';
import { FileResult, FileStatus, FileUpload, UploadStatus } from './file-upload.model';
import { FileUploadService } from './file-upload.service';

const DIRECTORY_HEURISTIC_THRESHOLD = 1024 * 1024; // 1 MB

@Component({
  selector: 'cp-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.less'],
  providers: [FileUploadService]
})
export class FileUploadComponent implements OnInit, OnChanges, OnDestroy {
  @Input() uploadTitle: string;

  @Input() uploadMessage: string;

  @Input() uploadUrl: (file: FileUpload) => string;

  @Input() deleteUrl: (file: FileUpload) => string;

  @Input() deleteAllUrl: () => string;

  @Input() maxFileSize = 4;

  @Input() required = true;

  @Input() validate = true;

  @ViewChild('fileInput') fileInput: ElementRef;

  errorMessage: string | undefined;
  private uploadSubscription: Subscription;

  get hasUploads() {
    return this.service.files && this.service.files.length > 0;
  }

  get hasErrors() {
    return this.validate && !!this.errorMessage;
  }

  constructor(public service: FileUploadService, private dialog: DialogService) {}

  ngOnInit() {
    this.service.uploadUrl = this.uploadUrl;
    this.service.deleteUrl = this.deleteUrl;
    this.service.deleteAllUrl = this.deleteAllUrl;
    this.service.uploadFinished$.subscribe(() => this.checkValidity());

    this.checkValidity();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['validate']) {
      this.checkValidity();
    }
  }

  ngOnDestroy() {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  showFileBrowser() {
    this.fileInput.nativeElement.click();
  }

  onFilesChosen($event: Event) {
    const target = $event.target as HTMLInputElement;
    if (target.files) {
      this.uploadFiles(target.files);
    }
    target.value = '';
  }

  removeFile(file: FileUpload) {
    this.service.remove(file);
    this.checkValidity();
  }

  uploadFiles(fileList: FileList) {
    if (!fileList || fileList.length === 0) {
      return;
    }

    Observable.from(fileList)
      .flatMap(file => this.uploadOrError(file))
      .reduce((results: FileResult[], current: FileResult) => [...results, current], [])
      .subscribe(results => this.processUploadResults(results));  
  
  }

  checkValidity() {
    if (this.required && this.service.files.length === 0) {
      this.errorMessage = 'Errors.atLeastOneFileRequired';
      return false;
    }

    for (const file of this.service.files) {
      if (file.status === UploadStatus.Failed || file.status === UploadStatus.Duplicate) {
        this.errorMessage = 'Errors.removeFailedUploads';
        return false;
      } else if (file.status !== UploadStatus.Complete) {
        this.errorMessage = 'Errors.waitForFileUpload';
        return false;
      }
    }

    this.errorMessage = undefined;
    return true;
  }

  private uploadOrError(file: File) {
    const result = {
      name: file.name,
      status: FileStatus.Successful
    };
    if (file.size > this.maxFileSizeBytes) {
      result.status |= FileStatus.ErrorTooLarge;
    }
    if (file.size < DIRECTORY_HEURISTIC_THRESHOLD) {
      return this.createUploaderObservable(file, result);
    } else {
      if (result.status === FileStatus.Successful) {
        this.service.upload(file);
      }
      return Observable.of(result);
    }
  }

  private createUploaderObservable(file: File, result: FileResult) {
    return Observable.defer<Observable<FileResult>>(() => {
      const reader = new FileReader();
      const observable = new Observable<FileResult>(observer => {
        reader.onerror = () => {
          result.status |= FileStatus.ErrorIsFolder;
          observer.next(result);
          observer.complete();
        };
        reader.onload = () => {
          if (result.status === FileStatus.Successful) {
            this.service.upload(file);
          }
          observer.next(result);
          observer.complete();
        };
      });
      reader.readAsArrayBuffer(file);
      return observable;
    });
  }

  private processUploadResults(fileResults: FileResult[]) {
    const sizeErrors: string[] = [];
    const folderErrors: string[] = [];
    for (const result of fileResults) {
      if ((result.status & FileStatus.ErrorTooLarge) !== 0) {
        sizeErrors.push(result.name);
      }
      if ((result.status & FileStatus.ErrorIsFolder) !== 0) {
        folderErrors.push(result.name);
      }
    }

    if (sizeErrors.length > 0 || folderErrors.length > 0) {
      const messages: DialogMessage[] = [];
      if (sizeErrors.length > 0) {
        messages.push({
          value: 'Errors.maximumFileSizeExceeded',
          params: {
            size: this.maxFileSize.toString(),
            files: sizeErrors.join(', ')
          }
        });
      }
      if (folderErrors.length > 0) {
        messages.push({
          value: 'Errors.noDirectoryUpload',
          params: {
            files: folderErrors.join(', ')
          }
        });
      }

      this.dialog.ok('Dialog.fileUploadWarning', ...messages).show();
    }
    this.checkValidity();
  }

  private get maxFileSizeBytes() {
    return this.maxFileSize * 1024 * 1024;
  }
}
